import type { IngatlanTipus, LeadStatusz } from '../lib/tipusok.ts';
import { folyoHonap } from './ujrahivas.ts';

export type Ful = 'nyitott' | 'lezart' | 'mind' | 'nem_ellenorzott';
export interface Szurok {
  ful: Ful; statusz: LeadStatusz | null; kereses: string | null; tipus: IngatlanTipus | null; kollega: string | null; minPontszam: number | null;
  forras: string | null; tol: string | null; ig: string | null;
  /** Csak az esedékes újrahívások: „később aktuális” lead, a hónapja a folyó vagy korábbi. */
  esedekes: boolean;
  oldal: number;
}
export interface SzurhetoLekerdezes {
  or(szuro: string): this; eq(m: string, v: unknown): this; in(m: string, v: unknown[]): this; gte(m: string, v: unknown): this; lt(m: string, v: unknown): this; lte(m: string, v: unknown): this;
}

export const FUL_STATUSZOK: Record<Ful, LeadStatusz[]> = {
  nyitott: ['uj', 'elerve', 'kesobb_aktualis', 'szemle_egyeztetve', 'szemle_megtortent', 'megbizas'],
  lezart: ['eladva', 'elveszett'],
  mind: ['uj', 'elerve', 'kesobb_aktualis', 'szemle_egyeztetve', 'szemle_megtortent', 'megbizas', 'eladva', 'elveszett'],
  nem_ellenorzott: ['nem_ellenorzott'],
};
const FULEK = Object.keys(FUL_STATUSZOK) as Ful[];
const MIND_STATUSZ: LeadStatusz[] = ['nem_ellenorzott', 'uj', 'elerve', 'kesobb_aktualis', 'szemle_egyeztetve', 'szemle_megtortent', 'megbizas', 'eladva', 'elveszett'];
const TIPUSOK: IngatlanTipus[] = ['lakas', 'haz', 'iroda', 'ipari', 'telek', 'garazs'];
const DATUM = /^\d{4}-\d{2}-\d{2}$/;
export const OLDALMERET = 50;

export function szurokUrlbol(sp: URLSearchParams): Szurok {
  const ful = sp.get('ful') as Ful | null;
  const statusz = sp.get('statusz') as LeadStatusz | null;
  const tipus = sp.get('tipus') as IngatlanTipus | null;
  const pont = Number(sp.get('minPontszam'));
  const oldal = Number(sp.get('oldal'));
  const datum = (k: string) => { const v = sp.get(k); return v && DATUM.test(v) ? v : null; };
  return {
    ful: ful && FULEK.includes(ful) ? ful : 'nyitott',
    statusz: statusz && MIND_STATUSZ.includes(statusz) ? statusz : null,
    kereses: sp.get('q')?.trim() || null,
    tipus: tipus && TIPUSOK.includes(tipus) ? tipus : null,
    kollega: sp.get('kollega') || null,
    minPontszam: sp.has('minPontszam') && Number.isInteger(pont) && pont >= 0 && pont <= 100 ? pont : null,
    forras: sp.get('forras')?.trim().toLowerCase() || null,
    tol: datum('tol'),
    ig: datum('ig'),
    esedekes: sp.get('esedekes') === '1',
    oldal: Number.isInteger(oldal) && oldal > 0 ? oldal : 0,
  };
}

export function szurokUrlbe(s: Szurok): URLSearchParams {
  const sp = new URLSearchParams();
  if (s.ful !== 'nyitott') sp.set('ful', s.ful);
  if (s.statusz) sp.set('statusz', s.statusz);
  if (s.kereses) sp.set('q', s.kereses);
  if (s.tipus) sp.set('tipus', s.tipus);
  if (s.kollega) sp.set('kollega', s.kollega);
  if (s.minPontszam !== null) sp.set('minPontszam', String(s.minPontszam));
  if (s.forras) sp.set('forras', s.forras);
  if (s.tol) sp.set('tol', s.tol);
  if (s.ig) sp.set('ig', s.ig);
  if (s.esedekes) sp.set('esedekes', '1');
  if (s.oldal > 0) sp.set('oldal', String(s.oldal));
  return sp;
}

const BP_IDO = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Budapest', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
});
/** Budapest UTC-eltolása (ms) a megadott pillanatban. */
function budapestiEltolas(utcMs: number): number {
  const r = Object.fromEntries(BP_IDO.formatToParts(utcMs).map((p) => [p.type, Number(p.value)])) as Record<string, number>;
  return Date.UTC(r.year!, r.month! - 1, r.day!, r.hour!, r.minute!, r.second!) - utcMs;
}

/** A budapesti nap kezdete UTC-ben (téli/nyári időszámítással; az eltolást a budapesti éjfél pillanatára számoljuk). */
export function budapestiNapKezdete(datum: string): string {
  const [e, h, n] = datum.split('-').map(Number) as [number, number, number];
  const utcEjfel = Date.UTC(e, h - 1, n);
  // Első közelítés: téli eltolás (UTC+1), majd a közelítés pillanatának tényleges eltolásával korrigálunk.
  const kozelites = utcEjfel - budapestiEltolas(utcEjfel - 3_600_000);
  return new Date(utcEjfel - budapestiEltolas(kozelites)).toISOString();
}
const kovetkezoNap = (d: string) => new Date(Date.parse(`${d}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

/**
 * A keresőmező PostgREST `or` szűrője (név, utca, irányítószám, telefon). A szűrőszintaxist törő karakterek
 * (vessző, zárójel, idézőjel, joker) szóközre cserélődnek; üres keresésre null (nincs szűrés).
 */
export function keresesSzuro(nyers: string | null): string | null {
  const t = (nyers ?? '').replace(/[,()*%"\\_]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  const reszek = [`nev.ilike.*${t}*`, `utca.ilike.*${t}*`, `iranyitoszam.ilike.*${t}*`];
  let szam = t.replace(/[\s-]/g, '');
  if (szam.startsWith('06')) szam = `+36${szam.slice(2)}`;
  if (/^\+?\d{3,}$/.test(szam)) reszek.push(`telefon.ilike.*${szam}*`);
  return reszek.join(',');
}

export function szurokAlkalmazasa<Q extends SzurhetoLekerdezes>(q: Q, s: Szurok, irodaId: string | null, nowMs = Date.now()): Q {
  let r = q;
  if (irodaId) r = r.eq('office_id', irodaId);
  const csoport = FUL_STATUSZOK[s.ful];
  r = s.statusz && csoport.includes(s.statusz) ? r.eq('statusz', s.statusz) : r.in('statusz', csoport);
  const kereses = keresesSzuro(s.kereses);
  if (kereses) r = r.or(kereses);
  if (s.tipus) r = r.eq('ingatlan_tipus', s.tipus);
  if (s.kollega) r = r.eq('assigned_to', s.kollega);
  if (s.minPontszam !== null) r = r.gte('pontszam', s.minPontszam);
  if (s.forras) r = r.eq('utm->>source', s.forras);
  if (s.tol) r = r.gte('created_at', budapestiNapKezdete(s.tol));
  if (s.ig) r = r.lt('created_at', budapestiNapKezdete(kovetkezoNap(s.ig)));
  if (s.esedekes) r = r.eq('statusz', 'kesobb_aktualis').lte('ujrahivas_honap', folyoHonap(nowMs));
  return r;
}
